import { useEffect, useRef, useState } from 'react'

const DRAG_THRESHOLD_PX = 6

export const PackageTabs = ({ tabs = [], activeKey, onChange }) => {
    const stripRef = useRef(null)
    const drag = useRef({ down: false, startX: 0, scrollLeft: 0, moved: false })
    const [hiddenTabs, setHiddenTabs] = useState({ left: false, right: false })

    // A side has hidden tabs while the first/last tab extends past the container's edge.
    useEffect(() => {
        const el = stripRef.current
        if (!el) return
        const update = () => {
            const box = el.getBoundingClientRect()
            const left = (el.firstElementChild?.getBoundingClientRect().left ?? box.left) < box.left
            const right = (el.lastElementChild?.getBoundingClientRect().right ?? box.right) > box.right
            setHiddenTabs((prev) => (prev.left === left && prev.right === right ? prev : { left, right }))
        }
        update()
        el.addEventListener('scroll', update, { passive: true })
        window.addEventListener('resize', update)
        return () => {
            el.removeEventListener('scroll', update)
            window.removeEventListener('resize', update)
        }
    }, [tabs.length])

    const onPointerDown = (e) => {
        const el = stripRef.current
        if (!el) return
        // Leave presses on the scrollbar (below the padding box) to the browser.
        if (e.clientY > el.getBoundingClientRect().top + el.clientHeight) return
        drag.current = { down: true, startX: e.clientX, scrollLeft: el.scrollLeft, moved: false }
    }
    const onPointerMove = (e) => {
        const el = stripRef.current
        if (!el || !drag.current.down) return
        const dx = e.clientX - drag.current.startX
        if (Math.abs(dx) > DRAG_THRESHOLD_PX) drag.current.moved = true
        el.scrollLeft = drag.current.scrollLeft - dx
    }
    const endDrag = () => { drag.current.down = false }

    const handleClick = (key) => {
        // Suppress the click that terminates a drag gesture.
        if (drag.current.moved) { drag.current.moved = false; return }
        onChange?.(key)
    }

    return (
        <div className="relative">
            <div
                ref={stripRef}
                role="tablist"
                aria-label="Packages"
                className="flex gap-2 overflow-x-auto [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 px-10 py-3 bg-white border-b border-gray-100 cursor-grab active:cursor-grabbing select-none"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={endDrag}
                onPointerLeave={endDrag}
            >
                {tabs.map((tab) => {
                    const selected = tab.key === activeKey
                    // Peek: size tabs so 3 (4 from sm) fit plus half of the next one. The visible run is
                    // the content box plus the right padding (100% + 2.5rem), so for N tabs with gap-2:
                    // w = (100% + 2.5rem - N * 0.5rem) / (N + 0.5). A tab never shrinks below its label
                    // (min-width: auto), so wider labels eat into the peek instead of being clipped.
                    return (
                        <button
                            key={tab.key}
                            role="tab"
                            type="button"
                            aria-selected={selected}
                            onClick={() => handleClick(tab.key)}
                            className={`shrink-0 basis-[calc((100%_+_1rem)/3.5)] sm:basis-[calc((100%_+_0.5rem)/4.5)] inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-full px-2 py-1.5 text-sm font-medium border transition-colors ${
                                selected ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                            }`}
                        >
                            {tab.dotClass && <span className={`h-2 w-2 rounded-full ${tab.dotClass}`} aria-hidden="true" />}
                            {tab.label}
                        </button>
                    )
                })}
            </div>
            {/* Scroll hints: fade tabs into the strip's white inside its px-10 padding, stop above the
                scrollbar (bottom-2), and let pointer events through. */}
            {hiddenTabs.left && (
                <div
                    data-testid="scroll-hint-left"
                    aria-hidden="true"
                    className="pointer-events-none absolute top-0 bottom-2 left-0 w-7 bg-gradient-to-r from-white to-transparent"
                />
            )}
            {hiddenTabs.right && (
                <div
                    data-testid="scroll-hint-right"
                    aria-hidden="true"
                    className="pointer-events-none absolute top-0 bottom-2 right-0 w-7 bg-gradient-to-l from-white to-transparent"
                />
            )}
        </div>
    )
}
